import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sparkles, Clock, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

/**
 * Banner shown on Calendar page listing pre-booked (AI-reserved) slots
 * waiting for salon confirmation. Salon must Confirm or Release within 5 min.
 */
const PendingReservationsBanner = ({ onConfirmed, services, staff }) => {
  const { API, getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [actingId, setActingId] = useState(null);
  const [tick, setTick] = useState(0);
  const intervalRef = useRef(null);

  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/slot-broadcasts/pending`, getAuthHeaders());
      setItems(res.data.items || []);
    } catch (err) {
      console.error('pending fetch error', err);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => {
    fetchPending();
    // Poll every 15s + tick every second for countdown
    const poll = setInterval(fetchPending, 15000);
    intervalRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      clearInterval(poll);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchPending]);

  const confirm = async (broadcast) => {
    setActingId(broadcast.id);
    try {
      // Prefer the service the customer chose via WhatsApp reply; fallback to first available
      const serviceId = broadcast.requested_service_id || services?.[0]?.id;
      const staffId = staff?.[0]?.id;
      const payload = { broadcast_id: broadcast.id };
      if (serviceId) payload.service_id = serviceId;
      if (staffId) payload.staff_id = staffId;

      const res = await axios.post(`${API}/slot-broadcasts/confirm`, payload, getAuthHeaders());
      toast.success('Slot confermato!', { description: 'Appuntamento creato e cliente avvisato.' });
      onConfirmed?.(res.data.appointment_id);
      fetchPending();
    } catch (err) {
      toast.error('Errore conferma', { description: err.response?.data?.detail || err.message });
    } finally {
      setActingId(null);
    }
  };

  const release = async (broadcast) => {
    setActingId(broadcast.id);
    try {
      await axios.post(`${API}/slot-broadcasts/${broadcast.id}/release`, {}, getAuthHeaders());
      toast.message('Prenotazione rilasciata');
      fetchPending();
    } catch (err) {
      toast.error('Errore rilascio', { description: err.response?.data?.detail || err.message });
    } finally {
      setActingId(null);
    }
  };

  // re-render every second by referencing tick
  void tick;

  if (items.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="pending-reservations-banner">
      {items.map((b) => {
        const reserved = b.reserved_by || {};
        const slot = parseISO(b.slot_datetime);
        const reservedAt = b.reserved_at ? parseISO(b.reserved_at) : null;
        const elapsedSec = reservedAt ? Math.floor((Date.now() - reservedAt.getTime()) / 1000) : 0;
        const remainingSec = Math.max(0, 5 * 60 - elapsedSec);
        const mm = Math.floor(remainingSec / 60);
        const ss = String(remainingSec % 60).padStart(2, '0');
        const isExpiring = remainingSec < 60;

        return (
          <Card
            key={b.id}
            className={`border-2 ${isExpiring ? 'border-orange-500 animate-pulse' : 'border-primary'} bg-primary/5`}
            data-testid={`pending-reservation-${b.id}`}
          >
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm sm:text-base truncate">
                        {reserved.customer_name || 'Cliente'} ha prenotato via AI
                      </p>
                      <Badge variant="secondary" className="text-[10px]">AI auto-prenota</Badge>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      {format(slot, "EEEE d MMM 'alle' HH:mm", { locale: it })}
                    </p>
                    {b.requested_service_name ? (
                      <p className="text-xs sm:text-sm mt-0.5">
                        <span className="text-muted-foreground">Servizio richiesto: </span>
                        <span className="font-semibold text-primary">{b.requested_service_name}</span>
                      </p>
                    ) : b.awaiting_service_selection ? (
                      <p className="text-xs sm:text-sm mt-0.5 text-orange-600 dark:text-orange-400 italic">
                        ⏳ In attesa scelta servizio dal cliente...
                      </p>
                    ) : b.service_name ? (
                      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                        Slot originale: {b.service_name}
                      </p>
                    ) : null}
                    <div className="flex items-center gap-1 mt-1.5">
                      <Clock className={`w-3.5 h-3.5 ${isExpiring ? 'text-orange-500' : 'text-muted-foreground'}`} />
                      <span className={`text-xs font-mono font-medium ${isExpiring ? 'text-orange-600' : 'text-muted-foreground'}`}>
                        Scade tra {mm}:{ss}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => release(b)}
                    disabled={actingId === b.id}
                    data-testid={`release-btn-${b.id}`}
                    className="text-destructive hover:text-destructive"
                  >
                    {actingId === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
                    Rifiuta
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => confirm(b)}
                    disabled={actingId === b.id || b.awaiting_service_selection}
                    data-testid={`confirm-btn-${b.id}`}
                    title={b.awaiting_service_selection ? 'Attendi che il cliente scelga il servizio' : ''}
                  >
                    {actingId === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                    Conferma
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default PendingReservationsBanner;
